<template>
  <b-modal
    ref="modal"
    size="lg"
    class="text-left"
    lazy
    @hide="onModalHide"
    @hidden="onModalHidden"
  >
    <template slot="modal-header">
      <h5 class="modal-title">
        {{
          jobQuestion.id
            ? `Edit JQ${jobQuestion.id}`
            : `Create New Job Question`
        }}
      </h5>
      <div class="pull-right">
        <b-button variant="default" @click="$refs.modal.hide(true)">
          Cancel
        </b-button>
        <b-button variant="info" @click="save">
          <template v-if="!saveLoading">Save</template>
          <template v-else>
            <b-spinner small />
            <span>Saving...</span>
          </template>
        </b-button>
      </div>
    </template>

    <b-row class="my-4">
      <b-col class="text-left question-text" md="6">
        <b-row class="my-3">
          <b-col sm="3">
            <label>Required</label>
          </b-col>
          <b-col sm="9" class="text-left">
            <b-form-checkbox
              v-model="$v.jobQuestion.isRequired.$model"
              :value="1"
              :unchecked-value="0"
            />
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Job Question Type</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-select
                v-model="$v.jobQuestion.type.$model"
                :state="validateState('type')"
                :options="jobQuestionTypeOptions"
                @change="onJobQuestionTypeChanged"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Job Question Talent Type</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-select
                v-model="$v.jobQuestion.talent_type.$model"
                :disabled="!jobQuestion.type"
                :state="validateState('talent_type')"
                :options="talentTypeOptions"
              >
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Label</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-input
                v-model="$v.jobQuestion.label.$model"
                type="text"
                :state="validateState('label')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Talent Label</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-input
                v-model="$v.jobQuestion.talent_label.$model"
                type="text"
                :state="validateState('talent_label')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Talent Short Label</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-input
                  v-model="$v.jobQuestion.talent_short_label.$model"
                  type="text"
                  :state="validateState('talent_short_label')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Chart title</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="invalidFeedback">
              <b-form-input
                v-model="$v.jobQuestion.chart_title.$model"
                type="text"
                :state="validateState('chart_title')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Display Order</label>
          </b-col>
          <b-col sm="9">
            <b-form-group :invalid-feedback="displayOrderInvalidFeedback">
              <b-form-input
                v-model="$v.jobQuestion.display_order.$model"
                type="number"
                step="1"
                min="-32768"
                max="32767"
                :state="validateState('display_order')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Member Preamble</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.jobQuestion.member_preamble.$model"
              type="text"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Talent Preamble</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.jobQuestion.talent_preamble.$model"
              type="text"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Member Prompt</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.jobQuestion.member_prompt.$model"
              type="text"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Talent Prompt</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.jobQuestion.talent_prompt.$model"
              type="text"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Member Explanation</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.jobQuestion.member_explanation.$model"
              rows="2"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Talent Explanation</label>
          </b-col>
          <b-col sm="9">
            <b-form-textarea
              v-model="$v.jobQuestion.talent_explanation.$model"
              rows="2"
            />
          </b-col>
        </b-row>

        <b-row class="my-3" v-show="jobQuestion.type === 'SLIDER'">
          <b-col sm="3">
            <label>Calculate Totals</label>
          </b-col>
          <b-col sm="9" class="text-left">
            <b-form-checkbox
              v-model="$v.jobQuestion.calculateTotals.$model"
              :value="1"
              :unchecked-value="0"
            />
            <b-alert
              show
              variant="warning"
              v-if="jobQuestion.calculateTotals === 1"
            >
              If Variables have the '%' suffix, this will mark the response
              invalid if variable values do not total 100%.
            </b-alert>
          </b-col>
        </b-row>

        <template v-if="hasChart">
          <b-row class="my-3">
            <b-col sm="3">
              <label>Chart Type</label>
            </b-col>
            <b-col sm="9">
              <b-form-group>
                <b-form-select
                  v-model="$v.jobQuestion.chart.chart_type.$model"
                  :options="chartTypeOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <chart-config
            v-if="!isPie"
            :chart_type="chartType"
            :config="chartConfig"
            @configChanged="onConfigChanged"
          />
        </template>

        <template v-if="showMaximumSelectionsSettings">
          <b-row class="my-3">
            <b-col sm="3">
              <label>Maximum Selections</label>
            </b-col>
            <b-col sm="9">
              <b-form-group
                :invalid-feedback="maximumSelectionsInvalidFeedback"
                description="If the Maximum Selections is 0, then the user can choose any number of options for the answer."
              >
                <b-form-input
                  v-model="$v.jobQuestion.maximum_selections.$model"
                  type="number"
                  :state="validateState('maximum_selections')"
                  min="0"
                  :max="notDeletedVariables.length"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </template>
        <b-row class="my-3" v-if="isRandomizeVariablesAvailable">
          <b-col sm="3">
            <label>Randomize variables</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.jobQuestion.randomize_variables.$model"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Active</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.jobQuestion.is_active.$model"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Can be related to study questions</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.jobQuestion.can_be_related_to_study_question.$model"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Required for Match</label>
          </b-col>
          <b-col sm="9">
            <b-form-checkbox
              :unchecked-value="0"
              :value="1"
              v-model="$v.jobQuestion.is_required_for_match.$model"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Is Enabled on the ICO Browser</label>
          </b-col>
          <b-col sm="9" class="text-left">
            <b-form-checkbox
              v-model="$v.jobQuestion.is_enabled_on_ico_browser.$model"
              :value="1"
              :unchecked-value="0"
            />
          </b-col>
        </b-row>
        <b-row class="my-3">
          <b-col sm="3">
            <label>Is Displayed on the Dashboard</label>
          </b-col>
          <b-col sm="9" class="text-left">
            <b-form-checkbox
              v-model="$v.jobQuestion.is_on_dashboard.$model"
              :value="1"
              :unchecked-value="0"
            />
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col sm="3">
            <label>Dashboard Display Order</label>
          </b-col>
          <b-col sm="9">
            <b-form-group
              :invalid-feedback="dashboardDisplayOrderInvalidFeedback"
            >
              <b-form-input
                v-model="$v.jobQuestion.dashboard_display_order.$model"
                type="number"
                step="1"
                min="-32768"
                max="32767"
                :state="validateState('dashboard_display_order')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="isSlider" class="my-3">
          <b-col>
            <b-form-group
              label="Talent Value Must Be"
              label-cols="3"
              :invalid-feedback="invalidFeedback"
              :state="validateState('matching_comparator')"
            >
              <b-form-select
                v-model="$v.jobQuestion.matching_comparator.$model"
                :state="validateState('matching_comparator')"
                :options="comparatorOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-3">
          <b-col>
            <b-form-group
              label="Version"
              label-cols="3"
              :invalid-feedback="versionInvalidFeedback"
              :state="validateState('version')"
            >
              <b-form-input
                v-model="$v.jobQuestion.version.$model"
                type="number"
                step="1"
                :min="jobQuestion.oldVersion"
                :disabled="!jobQuestion.id"
                :state="validateState('version')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>

      <!-- ---------------- variables -------------------- -->

      <b-col md="6">
        <JobQuestionEditVariables
          :job-question="$v.jobQuestion"
          :member-exclusive-id="memberExclusiveId"
          :talent-exclusive-id="talentExclusiveId"
          @update-member-exclusive="setMemberExclusive"
          @update-talent-exclusive="setTalentExclusive"
          @add-variable="addVariable"
          @delete-variable="deleteVariable"
          @order-variables="orderVariables"
          @add-variable-option="addVariableOption"
          @remove-option="onRemoveOption"
          @cancel-remove-option="onCancelRemoveOption"
        />
      </b-col>
    </b-row>

    <!-- ------------- preview -------------- -->

    <div slot="modal-footer">
      <h5 class="text-left">Job Question Preview</h5>
      <b-tabs
        v-model="tabIndex"
        nav-class="justify-content-end v2_phairify-tabs mb-4"
        active-nav-item-class="font-weight-bold text-uppercase text-danger"
        active-tab-class="font-weight-bold text-success"
      >
        <b-tab
          :title-link-class="[
            'phairify-tab',
            { 'phairify-tab-active': tabIndex === 0 },
          ]"
        >
          <div
            slot="title"
            :class="['px-lg-5', 'px-3', 'py-2']"
            v-b-tooltip.hover
            title="Select to see Preview Member Job Question."
          >
            Member
          </div>
          <b-card-text class="container">
            <job-question-preview
              :survey="{}"
              :job-question="jobQuestion"
              :job-question-variables="jobQuestion.jobQuestionVariables"
              :preview="true"
              :exclusive-id="memberExclusiveId"
              preview-type="member"
            />
          </b-card-text>
        </b-tab>
        <b-tab
          :title-link-class="[
            'phairify-tab',
            { 'phairify-tab-active': tabIndex === 1 },
          ]"
        >
          <div
            slot="title"
            :class="['px-lg-5', 'px-3', 'py-2']"
            v-b-tooltip.hover
            title="Select to see Preview Talent Job Question."
          >
            Talent
          </div>
          <b-card-text class="container">
            <job-question-preview
              :survey="{}"
              :job-question="jobQuestion"
              :job-question-variables="jobQuestion.jobQuestionVariables"
              :preview="true"
              :exclusive-id="talentExclusiveId"
              preview-type="talent"
            />
          </b-card-text>
        </b-tab>
      </b-tabs>
    </div>
  </b-modal>
</template>

<script>
import Api from '@/providers/Api';
import { validationMixin } from 'vuelidate';
import {
  required,
  integer,
  minValue,
  requiredIf,
  maxValue,
} from 'vuelidate/lib/validators';
import { showValidationErrorsAsToasted } from '@/helpers/ToastedHelper';
import Help from '@/providers/Help';
import JobQuestionEditVariables from '@/components/job/question/JobQuestionEditVariables';
import ChartConfig from '@/components/question/ChartConfig';
import JobQuestionPreview from '@/components/job/question/JobQuestionPreview';
import {
  CHART_TYPES,
  COMPARATOR_TYPES_LABELS,
  JOB_QUESTION_DEFAULT_CHART_TYPE,
  JOB_QUESTION_TALENT_TYPE_DEPENDENCY,
  JOB_QUESTION_TYPES,
  MATCHING_COMPARATORS,
} from '@/store';
import maxBy from 'lodash/maxBy';
import { createNewVariableOption } from '@/helpers/VariableOptionHelper';
import { compareQuestionVarsByOrder } from '@/helpers/questionnaireHelper';

export default {
  name: 'JobQuestionModalEditForm',
  mixins: [validationMixin],
  components: {
    JobQuestionEditVariables,
    ChartConfig,
    JobQuestionPreview,
  },
  props: {
    initialJobQuestion: {
      validator: (prop) => typeof prop === 'object' || prop === null,
      default: null,
    },
  },
  data: function () {
    const jobQuestion = {
      ...this.initialJobQuestion,
      jobQuestionVariables: (
        this.initialJobQuestion.jobQuestionVariables || []
      ).sort(compareQuestionVarsByOrder),
    };
    const getMemberExclusiveId = () => {
      if (this.initialJobQuestion.type !== JOB_QUESTION_TYPES.SELECT_ANY)
        return null;
      const exclusiveVariable = (
        this.initialJobQuestion.jobQuestionVariables || []
      ).find((v) => v.is_exclusive_member);
      return exclusiveVariable
        ? exclusiveVariable.id
          ? exclusiveVariable.id
          : exclusiveVariable.uid
        : null;
    };
    const getTalentExclusiveId = () => {
      if (this.initialJobQuestion.type !== JOB_QUESTION_TYPES.SELECT_ANY)
        return null;
      const exclusiveVariable = (
        this.initialJobQuestion.jobQuestionVariables || []
      ).find((v) => v.is_exclusive_talent);
      return exclusiveVariable
        ? exclusiveVariable.id
          ? exclusiveVariable.id
          : exclusiveVariable.uid
        : null;
    };
    return {
      saveLoading: false,
      jobQuestion: {
        id: this.initialJobQuestion ? jobQuestion.id : null,
        isRequired: this.initialJobQuestion ? jobQuestion.required : 0,
        type: this.initialJobQuestion ? jobQuestion.type : '',
        talent_type: this.initialJobQuestion ? jobQuestion.talent_type : '',
        label: this.initialJobQuestion ? jobQuestion.label : '',
        talent_label: this.initialJobQuestion ? jobQuestion.talent_label : '',
        talent_short_label: this.initialJobQuestion ? jobQuestion.talent_short_label : '',
        chart_title: this.initialJobQuestion ? jobQuestion.chart_title : '',
        member_preamble: this.initialJobQuestion
          ? jobQuestion.member_preamble
          : '',
        talent_preamble: this.initialJobQuestion
          ? jobQuestion.talent_preamble
          : '',
        member_prompt: this.initialJobQuestion ? jobQuestion.member_prompt : '',
        talent_prompt: this.initialJobQuestion ? jobQuestion.talent_prompt : '',
        member_explanation: this.initialJobQuestion
          ? jobQuestion.member_explanation
          : '',
        talent_explanation: this.initialJobQuestion
          ? jobQuestion.talent_explanation
          : '',
        calculateTotals: this.initialJobQuestion
          ? jobQuestion.calculate_totals
          : 0,
        jobQuestionVariables:
          this.initialJobQuestion &&
          this.initialJobQuestion.jobQuestionVariables
            ? jobQuestion.jobQuestionVariables
            : [],
        chart:
          this.initialJobQuestion && this.initialJobQuestion.chart
            ? jobQuestion.chart
            : {},
        maximum_selections: this.initialJobQuestion
          ? jobQuestion.maximum_selections
          : 0,
        randomize_variables: this.initialJobQuestion
          ? this.initialJobQuestion.randomize_variables
          : 0,
        is_active: this.initialJobQuestion
          ? this.initialJobQuestion.is_active
          : 1,
        can_be_related_to_study_question: this.initialJobQuestion
          ? this.initialJobQuestion.can_be_related_to_study_question
          : 0,
        is_required_for_match: this.initialJobQuestion
          ? this.initialJobQuestion.is_required_for_match
          : 0,
        is_enabled_on_ico_browser: this.initialJobQuestion
          ? this.initialJobQuestion.is_enabled_on_ico_browser
          : 0,
        display_order: this.initialJobQuestion
          ? this.initialJobQuestion.display_order
          : 0,
        is_on_dashboard: this.initialJobQuestion
          ? this.initialJobQuestion.is_on_dashboard
          : 0,
        dashboard_display_order: this.initialJobQuestion
          ? this.initialJobQuestion.dashboard_display_order
          : 0,
        version: (this.initialJobQuestion || {}).version || 1,
        oldVersion: (this.initialJobQuestion || {}).version || 1,
        matching_comparator:
          (this.initialJobQuestion || {}).matching_comparator || null,
      },
      memberExclusiveId: getMemberExclusiveId(),
      talentExclusiveId: getTalentExclusiveId(),
      tabIndex: 0,
    };
  },
  validations: function () {
    let variableOptions = {};
    if (
      this.jobQuestion.type === JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE
    ) {
      variableOptions = {
        required,
        minLength: (options) => {
          if (!options) return false;
          const notDeletedOptions = options.filter((o) => !o.delete_me);
          return notDeletedOptions.length >= 1;
        },
        $each: {
          name: {
            required: requiredIf((model) => !model.delete_me),
          },
          value: {
            required: requiredIf((model) => !model.delete_me),
          },
        },
      };
    }
    return {
      jobQuestion: {
        isRequired: {},
        talent_label: {},
        talent_short_label: {},
        member_preamble: {},
        talent_preamble: {},
        member_prompt: {},
        talent_prompt: {},
        member_explanation: {},
        talent_explanation: {},
        calculateTotals: {},
        randomize_variables: {},
        is_active: {},
        can_be_related_to_study_question: {},
        is_required_for_match: {},
        is_enabled_on_ico_browser: {},
        is_on_dashboard: {},
        matching_comparator: {},
        jobQuestionVariables: {
          $each: {
            id: {},
            uid: {},
            delete_me: {},
            visible: {},
            order: {},
            input_label: {},
            dv_name: {},
            prefix: {},
            suffix: {},
            singular_suffix: {},
            short_label: {},
            max: {},
            min: {},
            max_label: {},
            min_label: {},
            step: {},
            is_exclusive_member: {},
            is_exclusive_talent: {},
            is_talent_hidden: {},
            variableOptions,
          },
        },
        chart: {
          events: {
            render: () => this.$emit('chart-rendered'),
          },
          chart_type: {},
        },
        type: {
          required,
        },
        talent_type: {
          required,
        },
        label: {
          required,
        },
        chart_title: {
          required,
        },
        maximum_selections: {
          integer,
          minValue: minValue(0),
          lessOrEqualThanVariables: (value, jq) => {
            if (jq.type !== JOB_QUESTION_TYPES.SELECT_ANY) return true;
            const notDeletedVariables = jq.jobQuestionVariables.filter(
              (v) => !v.delete_me
            );
            return parseInt(value) <= notDeletedVariables.length;
          },
        },
        display_order: {
          required,
          integer,
          minValue: minValue(-32768),
          maxValue: maxValue(32767),
        },
        dashboard_display_order: {
          required,
          integer,
          minValue: minValue(-32768),
          maxValue: maxValue(32767),
        },
        version: {
          required,
          integer,
          minValue: minValue(this.jobQuestion.oldVersion),
        },
      },
    };
  },
  created: function () {
    if (!this.jobQuestion.chart.chart_type) {
      this.$set(
        this.jobQuestion.chart,
        'chart_type',
        JOB_QUESTION_DEFAULT_CHART_TYPE[this.jobQuestion.type]
      );
    }
  },
  mounted: function () {
    this.showModal();
  },
  computed: {
    jobQuestionTypeOptions: function () {
      return this.$store.state.jobQuestionTypeOptions;
    },
    talentTypeOptions: function () {
      return JOB_QUESTION_TALENT_TYPE_DEPENDENCY[this.jobQuestion.type];
    },
    invalidFeedback: function () {
      return 'This field is required';
    },
    maximumSelectionsInvalidFeedback: function () {
      if (!this.$v.jobQuestion.maximum_selections.integer) {
        return 'Maximum Selections should be a number.';
      }
      if (!this.$v.jobQuestion.maximum_selections.minValue) {
        return "Maximum Selections can't be less than 0.";
      }
      if (!this.$v.jobQuestion.maximum_selections.lessOrEqualThanVariables) {
        return "Maximum Selections can't be more than the number of Variables.";
      }
      return null;
    },
    displayOrderInvalidFeedback: function () {
      if (!this.$v.jobQuestion.display_order.integer) {
        return 'Display Order should be a number.';
      }
      if (!this.$v.jobQuestion.display_order.maxValue) {
        return "Display Order can't be more than 32767.";
      }
      if (!this.$v.jobQuestion.display_order.minValue) {
        return "Display Order can't be less than -32768.";
      }
      return 'This field is required';
    },
    dashboardDisplayOrderInvalidFeedback: function () {
      if (!this.$v.jobQuestion.dashboard_display_order.integer) {
        return 'Dashboard Display Order should be a number.';
      }
      if (!this.$v.jobQuestion.dashboard_display_order.maxValue) {
        return "Dashboard Display Order can't be more than 32767.";
      }
      if (!this.$v.jobQuestion.dashboard_display_order.minValue) {
        return "Dashboard Display Order can't be less than -32768.";
      }
      return 'This field is required';
    },
    versionInvalidFeedback: function () {
      if (!this.$v.jobQuestion.version.integer) {
        return 'Version should be a number.';
      }
      if (!this.$v.jobQuestion.version.minValue) {
        return `Version should be equal or more than ${this.jobQuestion.oldVersion}.`;
      }
      return 'This field is required';
    },
    chartType: function () {
      return this.jobQuestion.chart.chart_type;
    },
    hasChart: function () {
      return this.jobQuestion.type !== JOB_QUESTION_TYPES.GEOGRAPHY;
    },
    chartConfig: function () {
      return this.jobQuestion.chart && this.jobQuestion.chart.chart_config
        ? this.jobQuestion.chart.chart_config
        : {};
    },
    chartTypeOptions: function () {
      return this.$store.state.chartTypeOptions.filter((o) => {
        return o.questionTypes.includes(this.jobQuestion.type);
      });
    },
    comparatorOptions: function () {
      return MATCHING_COMPARATORS.map((comparatorKey) => ({
        value: COMPARATOR_TYPES_LABELS[comparatorKey],
        text: COMPARATOR_TYPES_LABELS[comparatorKey],
      }));
    },
    isPie: function () {
      return (
        this.jobQuestion.chart &&
        this.jobQuestion.chart.chart_type === CHART_TYPES.PIE
      );
    },
    showMaximumSelectionsSettings: function () {
      return this.jobQuestion.type === JOB_QUESTION_TYPES.SELECT_ANY;
    },
    isRandomizeVariablesAvailable: function () {
      return [
        JOB_QUESTION_TYPES.SELECT_ANY,
        JOB_QUESTION_TYPES.SELECT_ONE,
        JOB_QUESTION_TYPES.SLIDER,
      ].includes(this.jobQuestion.type);
    },
    notDeletedVariables: function () {
      return this.jobQuestion.jobQuestionVariables.filter((v) => !v.delete_me);
    },
    isSlider: function () {
      return this.jobQuestion.type === JOB_QUESTION_TYPES.SLIDER;
    },
  },
  methods: {
    save: async function () {
      this.$v.$touch();

      if (this.$v.$anyError) {
        if (this.$v.jobQuestion.jobQuestionVariables.$anyError) {
          const collection =
            this.$v.jobQuestion.jobQuestionVariables.$each.$iter;
          for (let key in collection) {
            if (collection.hasOwnProperty(key) && collection[key].$anyError) {
              collection[key].$model.visible = true;
            }
          }
        }
        return;
      }

      this.saveLoading = true;

      const jobQuestionData = {
        ...this.initialJobQuestion,
        id: this.jobQuestion.id,
        required: this.jobQuestion.isRequired,
        label: this.jobQuestion.label,
        talent_label: this.jobQuestion.talent_label,
        chart_title: this.jobQuestion.chart_title,
        type: this.jobQuestion.type,
        talent_type: this.jobQuestion.talent_type,
        member_preamble: this.jobQuestion.member_preamble,
        talent_preamble: this.jobQuestion.talent_preamble,
        member_prompt: this.jobQuestion.member_prompt,
        talent_prompt: this.jobQuestion.talent_prompt,
        member_explanation: this.jobQuestion.member_explanation,
        talent_explanation: this.jobQuestion.talent_explanation,
        calculate_totals: this.jobQuestion.calculateTotals,
        jobQuestionVariables: this.getVariables(),
        chart: this.jobQuestion.chart,
        maximum_selections: this.jobQuestion.maximum_selections,
        is_active: this.jobQuestion.is_active,
        is_required_for_match: this.jobQuestion.is_required_for_match,
        is_enabled_on_ico_browser: this.jobQuestion.is_enabled_on_ico_browser,
        can_be_related_to_study_question:
          this.jobQuestion.can_be_related_to_study_question,
        display_order: this.jobQuestion.display_order,
        is_on_dashboard: this.jobQuestion.is_on_dashboard,
        dashboard_display_order: this.jobQuestion.dashboard_display_order,
        version: this.jobQuestion.version,
        matching_comparator: this.jobQuestion.matching_comparator,
      };

      if (this.isRandomizeVariablesAvailable) {
        jobQuestionData.randomize_variables =
          this.jobQuestion.randomize_variables;
      }

      try {
        const jq = await Api.updateJobQuestion(jobQuestionData);
        this.syncQuestion(jq);
        this.$toasted.success('Job Question saved', { duration: 3000 });
        this.$emit('jobQuestionSaved', jq);
      } catch (e) {
        this.showErrors(e);
      } finally {
        this.saveLoading = false;
        this.$v.$reset();
      }
    },
    syncQuestion: function (jq) {
      this.jobQuestion.id = jq.id;
      this.jobQuestion.isRequired = jq.required;
      this.jobQuestion.type = jq.type;
      this.jobQuestion.label = jq.label;
      this.jobQuestion.talent_label = jq.talent_label;
      this.jobQuestion.talent_short_label = jq.talent_short_label;
      this.jobQuestion.chart_title = jq.chart_title;
      this.jobQuestion.member_preamble = jq.member_preamble;
      this.jobQuestion.talent_preamble = jq.talent_preamble;
      this.jobQuestion.member_prompt = jq.member_prompt;
      this.jobQuestion.talent_prompt = jq.talent_prompt;
      this.jobQuestion.member_explanation = jq.member_explanation;
      this.jobQuestion.talent_explanation = jq.talent_explanation;
      this.jobQuestion.calculateTotals = jq.calculate_totals;
      this.jobQuestion.jobQuestionVariables = jq.jobQuestionVariables.sort(
        compareQuestionVarsByOrder
      );
      this.jobQuestion.chart = jq.chart;
      this.jobQuestion.maximum_selections = jq.maximum_selections;
      this.jobQuestion.randomize_variables = jq.randomize_variables;
      this.jobQuestion.is_active = jq.is_active;
      this.jobQuestion.can_be_related_to_study_question =
        jq.can_be_related_to_study_question;
      this.jobQuestion.is_enabled_on_ico_browser = jq.is_enabled_on_ico_browser;
      this.jobQuestion.display_order = jq.display_order;
      this.jobQuestion.is_on_dashboard = jq.is_on_dashboard;
      this.jobQuestion.dashboard_display_order = jq.dashboard_display_order;
      this.jobQuestion.is_required_for_match = jq.is_required_for_match;
      this.jobQuestion.version = jq.version;
      this.jobQuestion.oldVersion = jq.version;
      this.jobQuestion.matching_comparator = jq.matching_comparator;
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v.jobQuestion[name];
      return $dirty ? !$error : null;
    },
    showErrors: function (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(
          this.$toasted,
          error.response.data.errors
        );
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    showModal: function () {
      this.$refs.modal.show();
    },
    onModalHide: function (bvModalEvent) {
      if (this.saveLoading) {
        bvModalEvent.preventDefault();
      }
      if (this.$v.$anyDirty) {
        bvModalEvent.preventDefault();
        Help.confirm.show(
          'All unsaved data will be lost. Do you want continue?',
          'Continue',
          () => {
            this.$v.$reset();
            this.$refs.modal.hide();
          }
        );
      }
    },
    onModalHidden: function () {
      this.$emit('modalCancel');
    },
    onConfigChanged: function (config) {
      if (!this.jobQuestion.chart) {
        this.jobQuestion.chart = {};
      }
      this.$set(this.jobQuestion.chart, 'chart_config', config);
    },
    onJobQuestionTypeChanged: function () {
      if (!this.hasChart) {
        this.setEmptyConfig();
      }
      this.$set(
        this.jobQuestion.chart,
        'chart_type',
        JOB_QUESTION_DEFAULT_CHART_TYPE[this.jobQuestion.type]
      );
    },
    setEmptyConfig: function () {
      this.$set(this.jobQuestion.chart, 'chart_config', {});
    },
    setMemberExclusive: function (exclusiveId) {
      this.memberExclusiveId = exclusiveId;
      this.$v.jobQuestion.jobQuestionVariables.$touch();
    },
    setTalentExclusive: function (exclusiveId) {
      this.talentExclusiveId = exclusiveId;
      this.$v.jobQuestion.jobQuestionVariables.$touch();
    },
    getVariables: function () {
      const isExclusiveMember = ({ id, uid }) =>
        this.memberExclusiveId &&
        this.jobQuestion.type === JOB_QUESTION_TYPES.SELECT_ANY &&
        (id === this.memberExclusiveId || uid === this.memberExclusiveId);
      const isExclusiveTalent = ({ id, uid }) =>
        this.talentExclusiveId &&
        this.jobQuestion.type === JOB_QUESTION_TYPES.SELECT_ANY &&
        (id === this.talentExclusiveId || uid === this.talentExclusiveId);
      return this.jobQuestion.jobQuestionVariables.map((variable) => {
        return {
          ...variable,
          is_exclusive_member: isExclusiveMember(variable) ? 1 : 0,
          is_exclusive_talent: isExclusiveTalent(variable) ? 1 : 0,
        };
      });
    },
    addVariable: function () {
      const maxV = maxBy(this.jobQuestion.jobQuestionVariables, (v) => v.order);
      const variableId = Date.now();
      const newVariable = {
        uid: Date.now(),
        id: null,
        order: maxV ? maxV.order + 1 : 1,
        input_label: '[New Variable]',
        short_label: '',
        max: null,
        min: null,
        max_label: null,
        min_label: null,
        step: null,
        visible: true,
        delete_me: false,
        is_exclusive_member: 0,
        is_exclusive_talent: 0,
        is_talent_hidden: 0,
      };
      if (
        this.jobQuestion.type === JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE
      ) {
        newVariable.variableOptions = [createNewVariableOption(variableId)];
      }
      this.jobQuestion.jobQuestionVariables.push(newVariable);
      this.$v.jobQuestion.jobQuestionVariables.$touch();
    },
    deleteVariable: function (v) {
      this.$set(v, 'delete_me', !v.delete_me);
      this.orderVariables();
      this.$v.jobQuestion.jobQuestionVariables.$touch();
    },
    orderVariables: function () {
      this.jobQuestion.jobQuestionVariables
        .filter((v) => !v.delete_me)
        .forEach((v, i) => {
          this.$set(v, 'order', i + 1);
        });
    },
    addVariableOption: function (varId, option) {
      const varIndex = this.jobQuestion.jobQuestionVariables.findIndex(
        ({ id, uid }) => (id ? id === varId : uid === varId)
      );
      if (varIndex === -1) return;
      if (
        Array.isArray(
          this.jobQuestion.jobQuestionVariables[varIndex].variableOptions
        )
      ) {
        this.$set(
          this.jobQuestion.jobQuestionVariables[varIndex].variableOptions,
          this.jobQuestion.jobQuestionVariables[varIndex].variableOptions
            .length,
          option
        );
      } else {
        this.$set(
          this.jobQuestion.jobQuestionVariables[varIndex],
          'variableOptions',
          [option]
        );
      }
    },
    onRemoveOption: function (optionId, variableId) {
      this.setOptionDeleteMe(optionId, variableId);
    },
    onCancelRemoveOption: function (optionId, variableId) {
      this.setOptionDeleteMe(optionId, variableId, false);
    },
    setOptionDeleteMe: function (optionId, variableId, flag = true) {
      const varIndex = this.jobQuestion.jobQuestionVariables.findIndex(
        ({ id, uid }) => (id ? id === variableId : uid === variableId)
      );
      if (varIndex === -1) return;
      const optionIndex = this.jobQuestion.jobQuestionVariables[
        varIndex
      ].variableOptions.findIndex(
        (vo) => vo.id === optionId || vo.uid === optionId
      );
      if (optionIndex === -1) return;
      this.$set(
        this.jobQuestion.jobQuestionVariables[varIndex].variableOptions[
          optionIndex
        ],
        'delete_me',
        flag
      );
    },
  },
  watch: {
    'jobQuestion.chart.chart_type': function () {
      this.setEmptyConfig();
    },
    'jobQuestion.type': function (newType) {
      this.jobQuestion.randomize_variables = 0;
      this.exclusiveId = null;
      this.jobQuestion.talent_type = null;

      if (newType === JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE) {
        this.jobQuestion.jobQuestionVariables.forEach((variable) => {
          const variableId = variable.id || variable.uid;
          this.$set(variable, 'variableOptions', []);
          this.$set(
            variable.variableOptions,
            0,
            createNewVariableOption(variableId)
          );
        });
      } else {
        this.jobQuestion.jobQuestionVariables.forEach((variable) => {
          delete variable.variableOptions;
        });
      }
    },
  },
};
</script>

<style scoped>
.question-edit {
  display: inline;
}

.custom-controls-stacked {
  min-height: 85px;
  max-height: 170px;
  overflow-y: scroll;
}

textarea {
  min-height: 60px;
}

.modal-footer > div {
  width: 100%;
}
</style>
